/**
 * Quote packet — everything a carrier asks for, generated from D1.
 *
 * Built for filling in web forms: sections follow the order carrier applications ask,
 * and each one copies to the clipboard as plain text. The static HTML docket this
 * replaces went stale the moment a policy changed; this cannot, because it is the
 * database.
 *
 * Deliberately NOT here: date of birth and driver's licence numbers. Those are vault
 * items by classification (PRD section 3) and do not get a column in this module, so the
 * applicant block points at the vault instead of embedding them.
 */
import { useMemo, useState } from 'react';
import { api, money } from '../../lib/api';
import type {
  InsuranceClaim,
  InsuranceRequirement,
  InsuranceTypeConfig,
  QuotePacket,
} from '../../shared/types';
import {
  Button,
  Card,
  Chip,
  ErrorState,
  Loading,
  PanelHeading,
  cx,
  requirementNeed,
  useResource,
} from './ui';

const KIND_WORD: Record<string, string> = {
  require: 'Must have',
  avoid: 'Must not have',
  prefer: 'Prefer',
};

const MONTHS = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];

/**
 * A date as a form expects it. Claims stored on the 1st are month-precision only — the
 * inventory records the month and nothing finer — so rendering "1 March 2023" would be
 * inventing a day. On an insurance application that is a misrepresentation, not a
 * rounding. Month-only dates render as "March 2023" and say so.
 */
function claimDate(iso: string | null): string {
  if (!iso) return 'date not on file';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m) return iso;
  if (d === 1) return `${MONTHS[m - 1]} ${y} (exact day not on file)`;
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/** One numbered claim, written the way a claims-history box wants it. */
function claimLine(c: InsuranceClaim, i: number): string {
  const amt = c.amount_paid_cents != null ? `${money(c.amount_paid_cents)} paid` : 'amount not on file';
  const bits = [
    `${i + 1}) ${claimDate(c.incident_date)} — ${c.claim_type ?? 'Claim'}`,
    c.description ?? null,
    amt,
    c.status === 'settled' ? 'Closed.' : null,
  ].filter(Boolean);
  return bits.join('. ').replace(/\.\./g, '.');
}

/** The full claims-history answer: the losses, then what changed because of them. */
function claimsText(packet: QuotePacket): string {
  if (!packet.claims.length) {
    return `No losses in the past ${packet.claims_window_years} years.`;
  }
  const head = `${packet.claims.length} claim${packet.claims.length === 1 ? '' : 's'} in the past ${packet.claims_window_years} years, all closed:`;
  const list = packet.claims
    .slice()
    .sort((a, b) => (a.incident_date ?? '').localeCompare(b.incident_date ?? ''))
    .map(claimLine)
    .join('\n\n');
  const protection = packet.property?.detail['Protection'];
  const tail = protection
    ? `\n\nSince those losses: ${protection}. Both water claims were plumbing failures, and that failure mode is now detected within minutes.`
    : '';
  const latest = packet.claims
    .map((c) => c.incident_date ?? '')
    .sort()
    .pop();
  const since = latest ? ` No claims since ${claimDate(latest).replace(' (exact day not on file)', '')}.` : '';
  return `${head}\n\n${list}${tail}${since}`;
}

/** The property as a sentence, because a description box will not take a table. */
function propertyProse(packet: QuotePacket): string {
  const d = packet.property?.detail ?? {};
  const parts = [
    d['Occupancy'] ?? 'Single family, owner-occupied primary residence',
    d['Year built'] ? `Built ${d['Year built']}` : null,
    d['Square footage'] ? `${d['Square footage']} sq ft` : null,
    d['Stories'] ? `${d['Stories']} stories` : null,
    d['Construction'] ?? null,
    d['Roof shape'] && d['Roof material']
      ? `${d['Roof shape'].toLowerCase()} roof with ${d['Roof material'].toLowerCase()}`
      : null,
    d['Pool'] && d['Pool'].toLowerCase().startsWith('yes')
      ? `In-ground pool, ${d['Pool'].replace(/^yes\s*—\s*/i, '')}`
      : null,
    d['Estimated replacement cost'] ? `Estimated replacement cost ${d['Estimated replacement cost']}` : null,
  ].filter(Boolean);
  return `${packet.property?.name ?? ''}. ${parts.join('. ')}.`.replace(/\.\./g, '.');
}

/**
 * The roof answer, offered rather than extracted. Carriers pull aerial imagery before
 * they quote, so a hedge costs credibility on the rest of the file for nothing.
 */
function roofProse(packet: QuotePacket): string {
  const age = packet.property?.detail['Roof age'] ?? '';
  return (
    `Roof is ${age.toLowerCase()} — ${(packet.property?.detail['Roof material'] ?? 'asphalt shingle').toLowerCase()}, ` +
    `${(packet.property?.detail['Roof shape'] ?? 'hip').toLowerCase()} design, no known damage and no roof claims. ` +
    `We are actively obtaining replacement quotes and can commit to replacement as a condition of binding if that is required. ` +
    `Please quote both as-is and with a new roof so we can compare.`
  );
}

function requirementLine(r: InsuranceRequirement): string {
  const need = requirementNeed(r);
  return `${KIND_WORD[r.kind] ?? r.kind}: ${r.label}${need ? ` (${need})` : ''}`;
}

/** The lapse paragraph, written out so it goes onto every form the same way. */
function lapseText(packet: QuotePacket): string | null {
  if (packet.days_uninsured === null || !packet.prior) return null;
  const ended = packet.prior.expiration_date ?? 'an earlier date';
  return (
    `Prior ${packet.policy_type} coverage was NON-RENEWED BY THE CARRIER effective ${ended}, ` +
    `and the incumbent agency was unable to place replacement coverage in any of its markets. ` +
    `The property has been uninsured since that date (${packet.days_uninsured} days). ` +
    `There has been no cancellation for non-payment.`
  );
}

export default function PacketPanel({ types }: { types: InsuranceTypeConfig[] }) {
  const [policyType, setPolicyType] = useState(
    types.find((t) => t.slug === 'homeowners')?.slug ?? types[0]?.slug ?? 'homeowners'
  );

  const { data, loading, error, reload } = useResource<QuotePacket>(
    () => api.get<QuotePacket>(`/insurance/packet?policy_type=${encodeURIComponent(policyType)}`),
    [policyType]
  );

  const typeLabel = types.find((t) => t.slug === policyType)?.label ?? policyType;

  return (
    <section>
      <PanelHeading
        title="Quote packet"
        sub="Everything a carrier asks for, straight from the file. Copy each block into their form."
      />

      <div role="group" aria-label="Line of coverage" className="mb-5 flex flex-wrap gap-2">
        {types.map((t) => (
          <button
            key={t.slug}
            type="button"
            aria-pressed={t.slug === policyType}
            onClick={() => setPolicyType(t.slug)}
            className={cx(
              'min-h-11 rounded-full border px-4 text-sm font-medium transition-colors',
              'focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2',
              'focus-visible:ring-offset-canvas focus-visible:outline-none',
              t.slug === policyType
                ? 'border-sage bg-sage-soft text-sage'
                : 'border-border bg-surface text-ink-soft hover:bg-surface-sunk hover:text-ink'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && <Loading label={`Building the ${typeLabel} packet`} />}
      {error && !loading && <ErrorState message={error} onRetry={reload} />}
      {data && !loading && !error && <Body packet={data} typeLabel={typeLabel} />}
    </section>
  );
}

function Body({ packet, typeLabel }: { packet: QuotePacket; typeLabel: string }) {
  const lapse = lapseText(packet);
  const protection = packet.property?.detail['Protection'] ?? null;

  const propertyRows = useMemo(
    () => Object.entries(packet.property?.detail ?? {}).filter(([k]) => k !== 'Protection'),
    [packet.property]
  );

  return (
    <div className="space-y-4">
      {lapse && (
        <Card tone="over">
          <p className="font-display text-lg leading-snug font-semibold text-ink">
            Disclose the lapse on every form.
          </p>
          <p className="mt-1 max-w-prose text-sm text-ink-soft">
            {packet.days_uninsured} days uninsured. It shows in the CLUE database anyway, and an
            inaccurate application answer can void the policy or a claim. Non-renewal reads far
            better than a gap somebody had to find.
          </p>
        </Card>
      )}

      <Block
        title="Prior coverage & the lapse"
        hint="Paste verbatim into the free-text box"
        text={lapse ?? 'Coverage is in force — no lapse to declare.'}
      >
        <p className="max-w-prose text-sm leading-relaxed text-ink">
          {lapse ?? 'Coverage is in force — no lapse to declare.'}
        </p>
      </Block>

      {packet.property && (
        <Block
          title="Property"
          hint={`${propertyRows.length} fields`}
          text={
            `${packet.property.name}\n` +
            propertyRows.map(([k, v]) => `${k}: ${v}`).join('\n')
          }
        >
          <p className="mb-2 text-sm font-medium text-ink">{packet.property.name}</p>
          <dl className="grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
            {propertyRows.map(([k, v]) => (
              <div key={k} className="flex gap-2">
                <dt className="shrink-0 text-ink-soft">{k}</dt>
                <dd className="min-w-0 font-medium text-ink">{v}</dd>
              </div>
            ))}
          </dl>
        </Block>
      )}

      {packet.property && (
        <Block
          title="Property description"
          hint="For the free-text description box"
          text={propertyProse(packet)}
        >
          <p className="max-w-prose text-sm leading-relaxed text-ink">{propertyProse(packet)}</p>
        </Block>
      )}

      {packet.property?.detail['Roof age'] && (
        <Block
          title="Roof"
          hint="They will ask, and they can already see it"
          text={roofProse(packet)}
        >
          <p className="max-w-prose text-sm leading-relaxed text-ink">{roofProse(packet)}</p>
        </Block>
      )}

      {protection && (
        <Block
          title="Protection & mitigation"
          hint="Lead with this"
          tone="ok"
          text={protection}
        >
          <p className="max-w-prose text-sm leading-relaxed text-ink">{protection}</p>
          <p className="mt-2 max-w-prose text-xs leading-relaxed text-ink-soft">
            The last application recorded &ldquo;Alarm System: None&rdquo;, which was wrong. This
            answers the exact peril behind both water losses — get it onto every form.
          </p>
        </Block>
      )}

      <Block
        title={`Loss history — last ${packet.claims_window_years} years`}
        hint={`${packet.claims.length} ${packet.claims.length === 1 ? 'claim' : 'claims'}`}
        text={claimsText(packet)}
      >
        {packet.claims.length === 0 ? (
          <p className="text-sm text-ink-soft">No losses in the period.</p>
        ) : (
          <ul className="space-y-2">
            {packet.claims.map((c) => (
              <li key={c.id} className="text-sm">
                <span className="font-medium text-ink">
                  {claimDate(c.incident_date)} · {c.claim_type ?? 'Claim'} ·{' '}
                  {c.amount_paid_cents != null ? money(c.amount_paid_cents) : 'amount unknown'}
                </span>
                {c.description && <span className="text-ink-soft"> — {c.description}</span>}
                {c.notes && (
                  <p className="mt-0.5 max-w-prose text-xs leading-relaxed text-ink-soft">
                    {c.notes}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Block>

      {packet.requirements.length > 0 && (
        <Block
          title="Coverage to request"
          hint={`${packet.requirements.length} requirements`}
          text={packet.requirements.map(requirementLine).join('\n')}
        >
          <ul className="space-y-1.5">
            {packet.requirements.map((r) => (
              <li key={r.id} className="flex flex-wrap items-baseline gap-2 text-sm">
                <Chip tone={r.kind === 'prefer' ? 'neutral' : 'dusk'}>
                  {KIND_WORD[r.kind] ?? r.kind}
                </Chip>
                <span className="font-medium text-ink">{r.label}</span>
                {requirementNeed(r) && (
                  <span className="text-xs text-ink-soft">{requirementNeed(r)}</span>
                )}
              </li>
            ))}
          </ul>
        </Block>
      )}

      {packet.other_lines.length > 0 && (
        <Block
          title="Other lines they could quote"
          hint="Bundle discounts are among the largest available"
          text={packet.other_lines
            .map(
              (p) =>
                `${p.policy_type}: ${p.carrier}${p.policy_number ? ` (${p.policy_number})` : ''}` +
                `${p.annual_premium_cents != null ? ` — ${money(p.annual_premium_cents)}/yr` : ''}`
            )
            .join('\n')}
        >
          <ul className="space-y-1.5 text-sm">
            {packet.other_lines.map((p) => (
              <li key={p.id}>
                <span className="font-medium text-ink capitalize">{p.policy_type}</span>
                <span className="text-ink-soft">
                  {' '}
                  — {p.carrier}
                  {p.policy_number ? ` · ${p.policy_number}` : ''}
                  {p.annual_premium_cents != null
                    ? ` · ${money(p.annual_premium_cents)}/yr`
                    : ''}
                </span>
              </li>
            ))}
          </ul>
        </Block>
      )}

      {packet.other_assets.length > 0 && (
        <Block
          title="Vehicles & other assets"
          hint={`${packet.other_assets.length} on file`}
          text={packet.other_assets
            .map(
              (a) =>
                `${a.name}` +
                (Object.keys(a.detail).length
                  ? ` — ${Object.entries(a.detail).map(([k, v]) => `${k}: ${v}`).join(', ')}`
                  : '')
            )
            .join('\n')}
        >
          <ul className="space-y-1.5 text-sm">
            {packet.other_assets.map((a) => (
              <li key={a.id}>
                <span className="font-medium text-ink">{a.name}</span>
                {Object.entries(a.detail).map(([k, v]) => (
                  <span key={k} className="text-ink-soft">
                    {' '}
                    · {k}: {v}
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </Block>
      )}

      <Card tone="sunk">
        <p className="text-sm text-ink-soft">
          <span className="font-medium text-ink">Applicant identity is not in this packet.</span>{' '}
          Date of birth and licence numbers are vault items by classification, so they never get a
          column in this module. Pull them from the vault when a form asks.
        </p>
      </Card>

      <p className="max-w-prose text-xs leading-relaxed text-ink-soft">
        Everything above is what the file holds for {typeLabel.toLowerCase()}. Answer every
        application question truthfully — an inaccurate answer can void a policy or a claim. This
        is your own record, not professional advice.
      </p>
    </div>
  );
}

/** A copyable section. The copy button is the point — these go into web forms. */
function Block({
  title,
  hint,
  text,
  tone,
  children,
}: {
  title: string;
  hint?: string;
  text: string;
  tone?: 'ok';
  children: React.ReactNode;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard can be blocked by permissions or a non-secure context. Say so rather
      // than flashing a success state for something that didn't happen.
      setCopied(false);
      window.prompt('Copy manually:', text);
    }
  }

  return (
    <Card tone={tone === 'ok' ? 'ok' : 'surface'}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-baseline gap-2">
          <h3 className="font-display text-base font-semibold text-ink">{title}</h3>
          {hint && <span className="text-xs text-ink-soft">{hint}</span>}
        </div>
        <Button variant="secondary" onClick={copy} aria-live="polite">
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      {children}
    </Card>
  );
}
